import React, { useEffect, useMemo } from 'react';
import { Field } from 'formik';
import _ from 'lodash';
import {
  Section,
  FormGroup,
  StepperField,
  TextboxField,
  SelectDropdownField,
  Text,
  isForeignAccount,
} from '@tradingblock/components';
import {
  AllDeliveryTypes,
  DeliveryType,
  ApplicationStepProps,
  ApplicationType,
  ApplicationFormSection,
  AllBooleanToggleTypes,
  BooleanToggleType,
  AllEmploymentTypes,
  EmploymentType,
  ClearerType,
  PhoneModel,
} from '../../../types';
import { useData } from '../../../state/selectors';
import {
  PrimaryAccountHolderDisclosuresSection,
  JointAccountHolderDisclosuresSection,
} from './agreements/AccountHolderDisclosuresSection';
import { AccountDisclosuresSection } from './agreements/AccountDisclosuresSection';
import { AccountAgreementsSection } from './agreements/AccountAgreementsSection';
import {
  Country,
  getGroupedCountryOptions,
  AllTradeAuthorizationTypes,
  RQDTradeAuthorizationTypes,
} from '@tradingblock/types';
import { DateField, PersonField, PhoneField } from '../../fields';
import {
  updateFieldsDependentOnEmploymentTypeTradeAuth,
  updateFieldsDependentOnTradeAuthorization,
} from '../../../services';

export const AgreementsForm: React.FC<ApplicationStepProps> = ({
  values,
  setValues,
  getText,
  errors,
  submitCount,
  touched,
}) => {
  const visibleSections = useData(d => d.ui.visibleSections);
  const sections = useMemo(() => {
    let allSections: React.FC<ApplicationFormSection>[] = [];
    // primary account holder disclosures
    allSections = [...allSections, PrimaryAccountHolderDisclosuresSection];
    if (values.type === ApplicationType.Joint) {
      // joint account holder disclosures
      allSections = [...allSections, JointAccountHolderDisclosuresSection];
    }
    // account disclosures and agreements
    allSections = [...allSections, AccountDisclosuresSection, AccountAgreementsSection];

    return _.take(allSections, visibleSections || 1);
  }, [values.type, visibleSections]);

  // RQD cleared accounts default to electronic statement delivery and do not allow paper - ensure deliveryType is set
  useEffect(() => {
    if (values.clearer === ClearerType.RQD && values.deliveryType !== DeliveryType.Electronic) {
      setValues({ ...values, deliveryType: DeliveryType.Electronic });
    }
  }, [values.clearer, values.deliveryType]);

  // This will cleanup any old application data so that there is only one phone number for the party related to trading authorization.
  useEffect(() => {
    if (
      values.tradingAuthorization &&
      values.tradingAuthorization.phones &&
      values.tradingAuthorization.phones.length > 1
    ) {
      const updatedFormTradingAuthPhoneData: PhoneModel[] = [];

      const firstPhoneNumDataWithTypeAndNum = values.tradingAuthorization.phones.find(
        phoneData => phoneData.type && phoneData.phoneNumber
      );
      const firstPhoneNumDataWithTypeOnly = values.tradingAuthorization.phones.find(
        phoneData => phoneData.type && !phoneData.phoneNumber
      );

      if (firstPhoneNumDataWithTypeAndNum) {
        updatedFormTradingAuthPhoneData.push(firstPhoneNumDataWithTypeAndNum);
      } else if (firstPhoneNumDataWithTypeOnly) {
        updatedFormTradingAuthPhoneData.push(firstPhoneNumDataWithTypeOnly);
      }

      setValues({
        ...values,
        tradingAuthorization: { ...values.tradingAuthorization, phones: updatedFormTradingAuthPhoneData },
      });
    }
  }, []);

  return (
    <>
      <Section>
        {values.clearer !== ClearerType.RQD && (
          <>
            <h2 className="fields-title">{getText('deliveryType')}</h2>
            <p>{getText('deliveryType.fees')}</p>

            <FormGroup>
              <Field
                component={StepperField}
                id="deliveryType"
                options={AllDeliveryTypes}
              />
            </FormGroup>

            {values.deliveryType === DeliveryType.Paper && getText('deliveryType.fees', 'description')}
          </>
        )}

        {!isForeignAccount(values.primaryAccountHolder.address.country) && (
          <>
            <h2 className="fields-title mt-5">{getText('tradeAuthorization')}</h2>
            <p>{getText('tradeAuthorization', 'description')}</p>

            <FormGroup>
              <Field
                component={StepperField}
                id="tradeAuthorization"
                options={AllBooleanToggleTypes}
                defaultLabelId="boolean"
                onChanged={(value: BooleanToggleType) => {
                  setValues(updateFieldsDependentOnTradeAuthorization(values, value));
                }}
              />
            </FormGroup>
          </>
        )}

        {values.tradeAuthorization === BooleanToggleType.Yes && (
          <>
            <h2 className="fields-title">{getText('tradingAuthorization.type')}</h2>
            <p>
              {getText(
                values.clearer === ClearerType.RQD
                  ? 'tradingAuthorization.type.descriptionRQD'
                  : 'tradingAuthorization.type.descriptionApex'
              )}
            </p>
            <FormGroup
              className="mb-5 trade-auth-type-form-group"
              help={getText(
                values.clearer === ClearerType.RQD
                  ? 'tradingAuthorization.type.helpRQD'
                  : 'tradingAuthorization.type.helpApex'
              )}
            >
              <Field
                component={StepperField}
                id="tradingAuthorization.type"
                options={values.clearer === ClearerType.RQD ? RQDTradeAuthorizationTypes : AllTradeAuthorizationTypes}
                defaultLabelId="authorizationType"
              />
            </FormGroup>
            {values.tradingAuthorization && values.tradingAuthorization.type && (
              <>
                <h2 className="fields-title">{getText('tradingAuthorization.personal-info')}</h2>
                <FormGroup>
                  <Field component={PersonField} id="tradingAuthorization" personType="trade-authorization" />
                </FormGroup>

                <p>{getText('tradingAuthorization.phones', 'description')}</p>
                <FormGroup>
                  <Field component={PhoneField} id="tradingAuthorization.phones[0]" defaultLabelId="phone" />
                </FormGroup>

                <FormGroup>
                  <Field
                    component={SelectDropdownField}
                    id={`tradingAuthorization.citizenshipCountry`}
                    options={getGroupedCountryOptions(values && values.clearer)}
                    defaultLabelId="citizenshipCountry"
                    optionsDefaultLabelId="country"
                  />
                </FormGroup>

                {values.tradingAuthorization &&
                  values.tradingAuthorization.citizenshipCountry === Country.UnitedStatesOfAmerica && (
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        id={`tradingAuthorization.ssnSecret`}
                        label={<Text id="defaults.ssn" type="field" data={{ itin: <Text id="itin.label" /> }} />}
                        type="password"
                        allowUnmask={true}
                      />
                    </FormGroup>
                  )}

                {values.tradingAuthorization &&
                  values.tradingAuthorization.citizenshipCountry !== Country.UnitedStatesOfAmerica && (
                    <>
                      <FormGroup>
                        <Field
                          component={TextboxField}
                          id={`tradingAuthorization.foreignTaxIdNumber`}
                          defaultLabelId="taxIdentificationNumber"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field
                          component={SelectDropdownField}
                          id={`tradingAuthorization.foreignTaxIdCountry`}
                          options={getGroupedCountryOptions(values && values.clearer)}
                          defaultLabelId="treatyCountry"
                          optionsDefaultLabelId="country"
                        />
                      </FormGroup>
                    </>
                  )}

                <FormGroup>
                  <Field component={DateField} id={`tradingAuthorization.dateOfBirth`} defaultLabelId="dateOfBirth" />
                </FormGroup>

                <h2 className="fields-title">{getText('tradingAuthorization.employment')}</h2>
                <FormGroup>
                  <Field
                    component={StepperField}
                    id={`tradingAuthorization.employmentType`}
                    options={AllEmploymentTypes}
                    defaultLabelId="employmentType"
                    onChanged={(employmentType: EmploymentType) =>
                      setValues(updateFieldsDependentOnEmploymentTypeTradeAuth(values, employmentType))
                    }
                  />
                </FormGroup>
                {values.tradingAuthorization && values.tradingAuthorization.employmentType === EmploymentType.Employed && (
                  <>
                    <FormGroup>
                      <Field component={TextboxField} id={`tradingAuthorization.employer`} defaultLabelId="employer" />
                    </FormGroup>
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        id={`tradingAuthorization.jobPosition`}
                        defaultLabelId="jobPosition"
                      />
                    </FormGroup>
                  </>
                )}

                {values.tradingAuthorization && (
                  <>
                    <h2 className="fields-title">{getText('tradingAuthorization.disclosures')}</h2>
                    <p>{getText('tradingAuthorization.disclosures', 'description')}</p>
                    <p>{getText('tradingAuthorization.disclosures.industryEmployed', 'description')}</p>
                    <FormGroup>
                      <Field
                        component={StepperField}
                        id="tradingAuthorization.disclosures.industryEmployed"
                        options={AllBooleanToggleTypes}
                        defaultLabelId="boolean"
                      />
                    </FormGroup>
                    {values.tradingAuthorization.disclosures &&
                      values.tradingAuthorization.disclosures.industryEmployed === BooleanToggleType.Yes && (
                        <>
                          <FormGroup>
                            <Field
                              component={TextboxField}
                              id="tradingAuthorization.disclosuresIndustryEmployedFirmName"
                              defaultLabelId="disclosuresIndustryEmployedFirmName"
                            />
                          </FormGroup>
                        </>
                      )}

                    <p>{getText('tradingAuthorization.disclosures.stakeholder', 'description')}</p>
                    <FormGroup>
                      <Field
                        component={StepperField}
                        id="tradingAuthorization.disclosures.stakeholder"
                        options={AllBooleanToggleTypes}
                        defaultLabelId="boolean"
                      />
                    </FormGroup>
                    {values.tradingAuthorization.disclosures &&
                      values.tradingAuthorization.disclosures.stakeholder === BooleanToggleType.Yes && (
                        <>
                          <FormGroup>
                            <Field
                              component={TextboxField}
                              id="tradingAuthorization.disclosuresStakeholderTickerSymbol"
                              defaultLabelId="disclosuresStakeholderTickerSymbol"
                            />
                          </FormGroup>
                        </>
                      )}
                  </>
                )}
                <h2 className="fields-title">{getText('tradingAuthorization.signedName')}</h2>
                <FormGroup>
                  <Field
                    component={TextboxField}
                    id="tradingAuthorization.signedName"
                    defaultLabelId="tradeAuthSignedName"
                  />
                </FormGroup>
              </>
            )}
          </>
        )}
      </Section>

      {_.map(sections, (ChildSection, index) => (
        <ChildSection
          key={`ChildSection-${index}`}
          getText={getText}
          values={values}
          setValues={setValues}
          submitCount={submitCount}
          errors={errors}
          touched={touched}
        />
      ))}
    </>
  );
};
